/**
 * Croissant (https://mlcommons.org/croissant) helpers for the dataset onboarding flow:
 *  - validateCroissant: TypeScript port of Platform_Template/validate_croissant.py
 *  - croissantDocToFormValues / buildCroissantDocument: JSON-LD <-> "Croissant builder" form
 *  - buildCroissantTemplate: a minimal valid document users can download and edit
 */

import { metricNameToKey } from './metric-key.util';

export type JsonRecord = Record<string, unknown>;

export const CROISSANT_CONFORMS_TO = 'http://mlcommons.org/croissant/1.0';

export const CROISSANT_CONTEXT: JsonRecord = {
  '@language': 'en',
  '@vocab': 'https://schema.org/',
  cr: 'http://mlcommons.org/croissant/',
  dct: 'http://purl.org/dc/terms/',
  sc: 'https://schema.org/',
  citeAs: 'cr:citeAs',
  column: 'cr:column',
  conformsTo: 'dct:conformsTo',
  data: { '@id': 'cr:data', '@type': '@json' },
  dataType: { '@id': 'cr:dataType', '@type': '@vocab' },
  examples: { '@id': 'cr:examples', '@type': '@json' },
  extract: 'cr:extract',
  field: 'cr:field',
  fileObject: 'cr:fileObject',
  fileProperty: 'cr:fileProperty',
  fileSet: 'cr:fileSet',
  format: 'cr:format',
  includes: 'cr:includes',
  isLiveDataset: 'cr:isLiveDataset',
  jsonPath: 'cr:jsonPath',
  key: 'cr:key',
  md5: 'cr:md5',
  parentField: 'cr:parentField',
  path: 'cr:path',
  rai: 'http://mlcommons.org/croissant/RAI/',
  recordSet: 'cr:recordSet',
  references: 'cr:references',
  regex: 'cr:regex',
  repeated: 'cr:repeated',
  replace: 'cr:replace',
  samplingRate: 'cr:samplingRate',
  separator: 'cr:separator',
  source: 'cr:source',
  subField: 'cr:subField',
  equivalentProperty: 'cr:equivalentProperty',
  transform: 'cr:transform',
};

export const CROISSANT_DATA_TYPES = [
  'sc:Text',
  'sc:Integer',
  'sc:Float',
  'sc:Boolean',
  'sc:Date',
  'sc:URL',
  'sc:ImageObject',
  'cr:Label',
] as const;

export interface CroissantValidationResult {
  errors: string[];
  warnings: string[];
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is JsonRecord {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function asArray(value: unknown): unknown[] {
  if (Array.isArray(value)) {
    return value;
  }
  return value === undefined || value === null ? [] : [value];
}

function str(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** `sc:FileObject`, `cr:FileSet`, `https://schema.org/Dataset` -> `FileObject`, `FileSet`, `Dataset`. */
function bareType(value: unknown): string {
  const raw = str(Array.isArray(value) ? value[0] : value);
  return raw.replace(/^.*[:/#]/, '');
}

function idOf(node: unknown): string {
  if (!isRecord(node)) {
    return '';
  }
  return str(node['@id']) || str(node['name']);
}

function isEmpty(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    ((typeof value === 'string' || Array.isArray(value)) && value.length === 0) ||
    (isRecord(value) && Object.keys(value).length === 0) ||
    (typeof value === 'string' && value.trim().length === 0)
  );
}

// ---------------------------------------------------------------------------
// validation (port of validate_croissant.py)
// ---------------------------------------------------------------------------

/**
 * Validate a parsed Croissant document. Errors block the upload / submit; warnings are advisory.
 *
 * Same rules as validate_croissant.py, except:
 *  - the TANUH `platform` block (and the `tanuh` / `platform` @context entries it needs) is only
 *    checked when the document actually carries one, so plain Croissant files from other sources
 *    are accepted;
 *  - `mlcroissant` cannot run in the browser, so its structural checks (distribution / record set
 *    shape, resolvable references, fully-qualified inline-data keys) are performed here instead.
 */
export function validateCroissant(input: unknown): CroissantValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const err = (message: string) => errors.push(message);
  const warn = (message: string) => warnings.push(message);

  /** Require a key to be present and non-empty (validate_croissant.py `need`). */
  const need = (obj: JsonRecord, key: string, where: string, kind?: 'object' | 'array' | 'string') => {
    if (!(key in obj)) {
      err(`${where}: missing required key '${key}'`);
      return undefined;
    }
    const value = obj[key];
    if (isEmpty(value)) {
      err(`${where}.${key} is empty`);
      return undefined;
    }
    if (kind === 'object' && !isRecord(value)) {
      err(`${where}.${key} should be an object`);
      return undefined;
    }
    if (kind === 'array' && !Array.isArray(value)) {
      err(`${where}.${key} should be a list`);
      return undefined;
    }
    if (kind === 'string' && typeof value !== 'string') {
      err(`${where}.${key} should be text`);
      return undefined;
    }
    return value;
  };

  if (!isRecord(input)) {
    err('not a valid Croissant document: expected a JSON object at the top level');
    return { errors, warnings };
  }
  const doc = input;

  // ---- the standard Croissant half ----------------------------------------
  for (const key of ['@context', '@type', 'name', 'description', 'distribution', 'recordSet']) {
    need(doc, key, 'croissant');
  }

  const ctx = doc['@context'];
  if (ctx !== undefined && !isEmpty(ctx)) {
    if (!isRecord(ctx)) {
      err('@context must be an object defining the Croissant vocabulary (copy it from the template)');
    } else if (!ctx['cr'] || !ctx['sc']) {
      warn('@context does not define the "cr" and "sc" prefixes — Croissant terms may not resolve');
    }
  }

  if (!isEmpty(doc['@type']) && bareType(doc['@type']) !== 'Dataset') {
    err(`croissant.@type should be "sc:Dataset" (got ${JSON.stringify(doc['@type'])})`);
  }
  if ('name' in doc && typeof doc['name'] !== 'string') {
    err('croissant.name must be text');
  }

  const conforms = asArray(doc['conformsTo']).map(str);
  if (conforms.length === 0) {
    warn(`no conformsTo — add "${CROISSANT_CONFORMS_TO}"`);
  } else if (!conforms.some((value) => value.startsWith('http://mlcommons.org/croissant/'))) {
    err(`croissant.conformsTo should be "${CROISSANT_CONFORMS_TO}" (got ${JSON.stringify(doc['conformsTo'])})`);
  }
  if (isEmpty(doc['license'])) {
    warn('no license — consumers will not know how they may use this dataset');
  }

  // ---- distribution -------------------------------------------------------
  const distributions = asArray(doc['distribution']);
  const distributionIds = new Set<string>();
  distributions.forEach((node, i) => {
    const where = `distribution[${i}]`;
    if (!isRecord(node)) {
      err(`${where} is not an object`);
      return;
    }
    const kind = bareType(node['@type']);
    if (kind !== 'FileObject' && kind !== 'FileSet') {
      err(`${where}.@type must be sc:FileObject or cr:FileSet (got ${JSON.stringify(node['@type'])})`);
    }
    need(node, 'name', where);
    const id = idOf(node);
    if (id) {
      if (distributionIds.has(id)) {
        err(`${where}: duplicate id '${id}'`);
      }
      distributionIds.add(id);
    }
    need(node, 'encodingFormat', where);
    if (kind === 'FileObject') {
      need(node, 'contentUrl', where);
      if (isEmpty(node['sha256']) && isEmpty(node['md5'])) {
        warn(`${where}: no sha256 / md5 checksum — consumers cannot verify the file`);
      }
    } else if (kind === 'FileSet') {
      need(node, 'includes', where);
    }
  });

  // ---- recordSet ----------------------------------------------------------
  const recordSets = asArray(doc['recordSet']);
  const recordSetIds = new Set<string>();
  const fieldIds = new Set<string>();
  recordSets.forEach((rs) => {
    if (!isRecord(rs)) {
      return;
    }
    const rsId = idOf(rs);
    recordSetIds.add(rsId);
    asArray(rs['field']).forEach((field) => {
      if (isRecord(field)) {
        fieldIds.add(str(field['@id']) || `${rsId}/${str(field['name'])}`);
      }
    });
  });

  const seenRecordSets = new Set<string>();
  recordSets.forEach((rs, i) => {
    const where = `recordSet[${i}]`;
    if (!isRecord(rs)) {
      err(`${where} is not an object`);
      return;
    }
    if (bareType(rs['@type']) !== 'RecordSet') {
      err(`${where}.@type must be cr:RecordSet (got ${JSON.stringify(rs['@type'])})`);
    }
    need(rs, 'name', where);
    const rsId = idOf(rs);
    if (rsId) {
      if (seenRecordSets.has(rsId)) {
        err(`${where}: duplicate id '${rsId}'`);
      }
      seenRecordSets.add(rsId);
    }

    const fields = need(rs, 'field', where, 'array') as unknown[] | undefined;
    const ownFieldIds = new Set<string>();
    (fields ?? []).forEach((field, j) => {
      const fw = `${where}.field[${j}]`;
      if (!isRecord(field)) {
        err(`${fw} is not an object`);
        return;
      }
      need(field, 'name', fw);
      const fieldId = str(field['@id']) || `${rsId}/${str(field['name'])}`;
      if (ownFieldIds.has(fieldId)) {
        err(`${fw}: duplicate field id '${fieldId}'`);
      }
      ownFieldIds.add(fieldId);
      if (isEmpty(field['dataType']) && isEmpty(field['subField'])) {
        err(`${fw}: missing required key 'dataType'`);
      }
      const source = field['source'];
      if (isRecord(source)) {
        for (const refKey of ['fileObject', 'fileSet']) {
          const ref = idOf(source[refKey]);
          if (source[refKey] !== undefined && !distributionIds.has(ref)) {
            err(`${fw}.source.${refKey} refers to '${ref}', which is not in distribution`);
          }
        }
        if (isRecord(source['field'])) {
          const ref = idOf(source['field']);
          if (!fieldIds.has(ref) && !recordSetIds.has(ref)) {
            err(`${fw}.source.field refers to '${ref}', which is not a known field`);
          }
        }
      }
    });

    if (rs['data'] !== undefined) {
      if (!Array.isArray(rs['data'])) {
        err(`${where}.data must be a list of records`);
      } else {
        // Inline data keys must be fully qualified ("rs/field"), the short form is rejected.
        const badKeys = new Set<string>();
        rs['data'].forEach((record) => {
          if (isRecord(record)) {
            Object.keys(record).forEach((key) => {
              if (!ownFieldIds.has(key)) {
                badKeys.add(key);
              }
            });
          }
        });
        if (badKeys.size > 0) {
          err(
            `${where}.data keys must be fully qualified field ids (e.g. "${rsId}/name"); ` +
              `unrecognised: ${[...badKeys].slice(0, 5).join(', ')}`,
          );
        }
      }
    }
  });

  // ---- TANUH platform block (only when present) ---------------------------
  const platform = doc['platform'] ?? doc['tanuh:platform'];
  if (platform !== undefined) {
    validatePlatformBlock(doc, platform, recordSets, need, err, warn);
  }

  // ---- unfilled placeholders ----------------------------------------------
  const placeholders = (JSON.stringify(doc).match(/(?<![A-Za-z])TODO(?![a-z])/g) ?? []).length;
  if (placeholders > 0) {
    err(`${placeholders} unfilled 'TODO' placeholder(s) left in the file`);
  }

  return { errors, warnings };
}

type NeedFn = (
  obj: JsonRecord,
  key: string,
  where: string,
  kind?: 'object' | 'array' | 'string',
) => unknown;

function validatePlatformBlock(
  doc: JsonRecord,
  platform: unknown,
  recordSets: unknown[],
  need: NeedFn,
  err: (message: string) => void,
  warn: (message: string) => void,
): void {
  const ctx = doc['@context'];
  if (isRecord(ctx)) {
    if (ctx['tanuh'] === undefined) {
      err(
        '@context is missing "tanuh": "https://bodh.tanuh.ai/schema/1.0#" — ' +
          'without it the platform block is not legal JSON-LD',
      );
    }
    if (ctx['platform'] !== 'tanuh:platform') {
      err('@context is missing "platform": "tanuh:platform"');
    }
  }

  const testPoints = recordSets.find((rs) => isRecord(rs) && rs['@id'] === 'test_points');
  if (!testPoints) {
    warn(
      "no recordSet with @id 'test_points' — the site can still render the card and detail page, " +
        'but nothing can iterate your individual test cases',
    );
  } else if (isRecord(testPoints) && isEmpty(testPoints['data'])) {
    warn(
      "test_points has no inline 'data' — consumers will have to fetch your CSVs separately " +
        'instead of reading this one file',
    );
  }

  if (!isRecord(platform)) {
    err("'platform' should be an object");
    return;
  }
  const p = platform;

  need(p, 'schema_version', 'platform');
  need(p, 'task', 'platform');

  const card = need(p, 'card', 'platform', 'object') as JsonRecord | undefined;
  if (card) {
    for (const key of ['title', 'summary', 'modality', 'volume', 'output']) {
      need(card, key, 'platform.card');
    }
    const volumeCount = card['volume_count'];
    if (typeof volumeCount !== 'number' || !Number.isInteger(volumeCount)) {
      err(
        'platform.card.volume_count must be an integer — it is how the site reports how many ' +
          'data points you provide',
      );
    } else if (volumeCount <= 0) {
      err('platform.card.volume_count must be greater than zero');
    }
  }

  // metrics
  const metrics = (need(p, 'metrics', 'platform', 'array') as unknown[] | undefined) ?? [];
  const roles: Record<string, JsonRecord[]> = {};
  const seenMetricIds = new Set<string>();
  metrics.forEach((metric, i) => {
    const where = `platform.metrics[${i}]`;
    if (!isRecord(metric)) {
      err(`${where} is not an object`);
      return;
    }
    const metricId = need(metric, 'id', where) as string | undefined;
    need(metric, 'name', where);
    need(metric, 'description', where);
    if (metricId !== undefined) {
      if (seenMetricIds.has(metricId)) {
        err(`${where}: duplicate metric id '${metricId}'`);
      }
      seenMetricIds.add(metricId);
    }
    const role = metric['role'];
    if (role !== 'primary' && role !== 'secondary' && role !== 'available') {
      err(`${where}.role must be primary, secondary or available (got ${JSON.stringify(role)})`);
    } else {
      (roles[role] ??= []).push(metric);
    }
    if ('higher_is_better' in metric && typeof metric['higher_is_better'] !== 'boolean') {
      err(`${where}.higher_is_better must be true or false`);
    }
    asArray(metric['components']).forEach((component, j) => {
      if (!isRecord(component)) {
        return;
      }
      for (const key of ['id', 'name', 'unit', 'description']) {
        need(component, key, `${where}.components[${j}]`);
      }
    });
  });

  const primaries = roles['primary'] ?? [];
  if (primaries.length === 0) {
    err("no metric has role 'primary' — at least one is required to rank the leaderboard");
  }
  if (primaries.length > 1) {
    const ranks = primaries.map((metric) => metric['rank']);
    if (ranks.some((rank) => rank === undefined || rank === null)) {
      err("every primary metric needs a 'rank' (1 = Primary metric 1) when there is more than one");
    } else if (new Set(ranks).size !== ranks.length) {
      err(`primary metric ranks must be unique, got [${ranks.join(', ')}]`);
    }
  }
  if ((roles['secondary'] ?? []).length > 1) {
    warn(`${roles['secondary'].length} secondary metrics — the card shows them all, which may crowd it`);
  }

  // statistics
  const stats = p['statistics'];
  if (stats === undefined) {
    err('platform.statistics missing — use [] if you genuinely have no charts');
  } else if (!Array.isArray(stats)) {
    err('platform.statistics must be a list');
  } else if (stats.length === 0) {
    warn('no charts — the Dataset Statistics section will be empty');
  } else {
    const chartIds = new Set<string>();
    stats.forEach((chart, i) => {
      const where = `platform.statistics[${i}]`;
      if (!isRecord(chart)) {
        err(`${where} is not an object`);
        return;
      }
      const chartId = need(chart, 'id', where) as string | undefined;
      if (chartId !== undefined) {
        if (chartIds.has(chartId)) {
          err(`${where}: duplicate chart id '${chartId}'`);
        }
        chartIds.add(chartId);
      }
      need(chart, 'title', where);
      need(chart, 'type', where);
      let axesOk = true;
      for (const axisKey of ['x', 'y']) {
        const axis = need(chart, axisKey, where, 'object') as JsonRecord | undefined;
        if (!axis) {
          axesOk = false;
          continue;
        }
        if (!str(axis['label'])) {
          err(`${where}.${axisKey}.label is required — the frontend prints it on the axis`);
        }
        if (!Array.isArray(axis['ticks']) || axis['ticks'].length === 0) {
          err(`${where}.${axisKey}.ticks must be a non-empty list`);
          axesOk = false;
        }
      }
      const series = need(chart, 'series', where, 'array') as unknown[] | undefined;
      if (!series || !axesOk) {
        return;
      }
      const xTicks = (chart['x'] as JsonRecord)['ticks'] as unknown[];
      const yTicks = (chart['y'] as JsonRecord)['ticks'] as unknown[];
      series.forEach((s, j) => {
        const values = isRecord(s) ? s['values'] : undefined;
        if (!Array.isArray(values) || values.length === 0) {
          err(`${where}.series[${j}].values must be a non-empty list`);
          return;
        }
        if (values.length !== xTicks.length) {
          err(
            `${where}.series[${j}]: ${values.length} values but ${xTicks.length} x ticks — ` +
              'they must line up one to one',
          );
        }
        if (values.some((v) => typeof v !== 'number')) {
          err(`${where}.series[${j}].values must all be numbers`);
          return;
        }
        const yMax = yTicks[yTicks.length - 1];
        const largest = Math.max(...(values as number[]));
        if (typeof yMax === 'number' && largest > yMax) {
          err(`${where}: largest value ${largest} exceeds the top y tick ${yMax} — bars would overflow the plot area`);
        }
      });
      const first = isRecord(series[0]) ? series[0]['values'] : undefined;
      if ('total' in chart && Array.isArray(first)) {
        const sum = first.filter((v): v is number => typeof v === 'number').reduce((a, b) => a + b, 0);
        if (chart['total'] !== sum) {
          err(`${where}.total is ${chart['total']} but the values sum to ${sum}`);
        }
      }
    });
  }

  // input / output
  const input = need(p, 'input', 'platform', 'object') as JsonRecord | undefined;
  if (input) {
    for (const key of ['data_type', 'count', 'format', 'max_file_size']) {
      need(input, key, 'platform.input');
    }
    const count = input['count'];
    const volumeCount = isRecord(card) ? card['volume_count'] : undefined;
    if (typeof count !== 'number' || !Number.isInteger(count)) {
      err('platform.input.count must be an integer (how many test cases you ship)');
    } else if (typeof volumeCount === 'number' && count !== volumeCount) {
      err(`platform.input.count (${count}) disagrees with platform.card.volume_count (${volumeCount})`);
    }
  }
  const output = need(p, 'output', 'platform', 'object') as JsonRecord | undefined;
  if (output) {
    for (const key of ['data_type', 'format', 'description']) {
      need(output, key, 'platform.output');
    }
  }
}

// ---------------------------------------------------------------------------
// Croissant document <-> builder form
// ---------------------------------------------------------------------------

export interface CroissantDistributionFormValue {
  /** Original @id when the entry came from an uploaded document (kept so references stay valid). */
  id: string;
  kind: 'FileObject' | 'FileSet';
  name: string;
  description: string;
  encodingFormat: string;
  contentUrl: string;
  includes: string;
  sha256: string;
}

export interface CroissantFieldFormValue {
  id: string;
  name: string;
  dataType: string;
  description: string;
  /** Name of the distribution entry the values are read from. */
  sourceDistribution: string;
  /** Column (CSV) the values are read from. */
  sourceColumn: string;
}

export interface CroissantRecordSetFormValue {
  id: string;
  name: string;
  description: string;
  fields: CroissantFieldFormValue[];
}

export interface CroissantFormValues {
  croissantDescription: string;
  croissantVersion: string;
  croissantLicense: string;
  croissantUrl: string;
  croissantCiteAs: string;
  croissantCreator: string;
  croissantDatePublished: string;
  croissantKeywords: string;
  croissantDistributions: CroissantDistributionFormValue[];
  croissantRecordSets: CroissantRecordSetFormValue[];
}

export function emptyDistribution(): CroissantDistributionFormValue {
  return { id: '', kind: 'FileObject', name: '', description: '', encodingFormat: '', contentUrl: '', includes: '', sha256: '' };
}

export function emptyField(): CroissantFieldFormValue {
  return { id: '', name: '', dataType: 'sc:Text', description: '', sourceDistribution: '', sourceColumn: '' };
}

export function emptyRecordSet(): CroissantRecordSetFormValue {
  return { id: '', name: '', description: '', fields: [emptyField()] };
}

export function emptyCroissantFormValues(): CroissantFormValues {
  return {
    croissantDescription: '',
    croissantVersion: '1.0.0',
    croissantLicense: '',
    croissantUrl: '',
    croissantCiteAs: '',
    croissantCreator: '',
    croissantDatePublished: new Date().toISOString().slice(0, 10),
    croissantKeywords: '',
    croissantDistributions: [emptyDistribution()],
    croissantRecordSets: [emptyRecordSet()],
  };
}

function creatorNames(creator: unknown): string {
  return asArray(creator)
    .map((entry) => (isRecord(entry) ? str(entry['name']) : str(entry)))
    .filter(Boolean)
    .join(', ');
}

function splitList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

/** A source the builder can represent: one distribution entry plus an optional CSV column. */
function readSimpleSource(
  source: unknown,
  distributionName: (id: string) => string,
): { sourceDistribution: string; sourceColumn: string } | null {
  if (!isRecord(source)) {
    return null;
  }
  const ref = isRecord(source['fileObject']) ? source['fileObject'] : source['fileSet'];
  if (!isRecord(ref)) {
    return null;
  }
  const extract = source['extract'];
  const column = isRecord(extract) ? str(extract['column']) : '';
  const extraKeys = Object.keys(source).filter((key) => !['fileObject', 'fileSet', 'extract'].includes(key));
  const extractExtra = isRecord(extract) && Object.keys(extract).some((key) => key !== 'column');
  if (extraKeys.length > 0 || extractExtra) {
    return null;
  }
  return { sourceDistribution: distributionName(idOf(ref)), sourceColumn: column };
}

/** Convert a (valid) Croissant document into the builder's form values. */
export function croissantDocToFormValues(doc: JsonRecord): CroissantFormValues {
  const distributions = asArray(doc['distribution']).filter(isRecord);
  const nameById = new Map(distributions.map((d) => [idOf(d), str(d['name'])]));
  const distributionName = (id: string) => nameById.get(id) ?? id;

  const croissantDistributions: CroissantDistributionFormValue[] = distributions.map((d) => ({
    id: str(d['@id']),
    kind: bareType(d['@type']) === 'FileSet' ? 'FileSet' : 'FileObject',
    name: str(d['name']),
    description: str(d['description']),
    encodingFormat: str(d['encodingFormat']),
    contentUrl: str(d['contentUrl']),
    includes: Array.isArray(d['includes']) ? d['includes'].map(str).join(', ') : str(d['includes']),
    sha256: str(d['sha256']),
  }));

  const croissantRecordSets: CroissantRecordSetFormValue[] = asArray(doc['recordSet'])
    .filter(isRecord)
    .map((rs) => ({
      id: str(rs['@id']),
      name: str(rs['name']),
      description: str(rs['description']),
      fields: asArray(rs['field'])
        .filter(isRecord)
        .map((field) => {
          const simple = readSimpleSource(field['source'], distributionName);
          return {
            id: str(field['@id']),
            name: str(field['name']),
            dataType: Array.isArray(field['dataType']) ? str(field['dataType'][0]) : str(field['dataType']),
            description: str(field['description']),
            sourceDistribution: simple?.sourceDistribution ?? '',
            sourceColumn: simple?.sourceColumn ?? '',
          };
        }),
    }));

  const keywords = asArray(doc['keywords']).map(str).filter(Boolean).join(', ');

  return {
    croissantDescription: str(doc['description']),
    croissantVersion: str(doc['version']),
    croissantLicense: typeof doc['license'] === 'string' ? str(doc['license']) : asArray(doc['license']).map(str).filter(Boolean).join(', '),
    croissantUrl: str(doc['url']),
    croissantCiteAs: str(doc['citeAs']),
    croissantCreator: creatorNames(doc['creator']),
    croissantDatePublished: str(doc['datePublished']).slice(0, 10),
    croissantKeywords: keywords,
    croissantDistributions: croissantDistributions.length > 0 ? croissantDistributions : [emptyDistribution()],
    croissantRecordSets: croissantRecordSets.length > 0 ? croissantRecordSets : [emptyRecordSet()],
  };
}

/** Builder values a TANUH `platform` block can pre-fill (only when the uploaded file has one). */
export function platformBlockToFormValues(doc: JsonRecord): JsonRecord {
  const platform = doc['platform'] ?? doc['tanuh:platform'];
  if (!isRecord(platform)) {
    return {};
  }
  const values: JsonRecord = {};
  const set = (key: string, value: unknown) => {
    const text = typeof value === 'number' ? String(value) : str(value);
    if (text) {
      values[key] = text;
    }
  };

  const card = isRecord(platform['card']) ? platform['card'] : {};
  set('shortDescription', card['summary']);
  set('modality', card['modality']);
  set('volume', card['volume']);
  set('output', card['output']);

  const input = isRecord(platform['input']) ? platform['input'] : {};
  set('inputDataType', input['data_type']);
  set('inputSize', input['size']);
  set('inputMaxFileSize', input['max_file_size']);
  set('inputFormat', input['format']);
  set('inputOrganization', input['organization']);
  set('inputPreprocessing', input['preprocessing']);

  const output = isRecord(platform['output']) ? platform['output'] : {};
  set('outputDataType', output['data_type']);
  set('outputFormat', output['format']);
  set('outputMaxFileSize', output['max_file_size']);
  set('outputColumns', Array.isArray(output['columns']) ? output['columns'].join(', ') : output['columns']);
  set('outputDescription', output['description']);

  const metrics = asArray(platform['metrics']).filter(isRecord);
  if (metrics.length > 0) {
    values['customMetrics'] = metrics.map((metric) => ({ name: str(metric['name']), min: '', max: '' }));
    const keys = metricKeys(metrics.map((metric) => str(metric['name'])));
    const byRank = (role: string) =>
      metrics
        .map((metric, index) => ({ metric, index }))
        .filter(({ metric }) => metric['role'] === role)
        .sort((a, b) => Number(a.metric['rank'] ?? 0) - Number(b.metric['rank'] ?? 0))[0];
    const primary = byRank('primary');
    const secondary = byRank('secondary');
    if (primary) {
      values['primaryMetric'] = keys[primary.index];
    }
    if (secondary) {
      values['secondaryMetric'] = keys[secondary.index];
    }
  }

  const statistics = asArray(platform['statistics']).filter(isRecord);
  if (statistics.length > 0) {
    values['distributionPlots'] = statistics.map((chart) => {
      const ticks = isRecord(chart['x']) ? asArray(chart['x']['ticks']) : [];
      const firstSeries = asArray(chart['series'])[0];
      const seriesValues = isRecord(firstSeries) ? asArray(firstSeries['values']) : [];
      return {
        label: str(chart['title']),
        note: str(chart['note']),
        total: typeof chart['total'] === 'number' ? chart['total'] : '',
        categories: ticks.map((tick, index) => ({
          name: String(tick),
          count: typeof seriesValues[index] === 'number' ? seriesValues[index] : '',
          description: '',
        })),
      };
    });
  }
  return values;
}

/** Same key derivation as the builder's metric dropdown (metricNameToKey, de-duplicated). */
function metricKeys(names: string[]): string[] {
  const seen = new Set<string>();
  return names.map((name, index) => {
    let key = metricNameToKey(name) || `metric_${index + 1}`;
    while (seen.has(key)) {
      key = `${key}_${index + 1}`;
    }
    seen.add(key);
    return key;
  });
}

function setOrDelete(target: JsonRecord, key: string, value: unknown): void {
  if (isEmpty(value)) {
    delete target[key];
  } else {
    target[key] = value;
  }
}

/**
 * Build the Croissant JSON-LD document from the builder's form values.
 *
 * `base` is the document the user uploaded (if any): everything the builder has no control for —
 * RAI properties, inline record data, checksums, transforms on a field's source, … — is carried
 * over instead of being silently dropped.
 *
 * The exception is a TANUH `platform` block: the builder's own benchmark sections are its editable
 * form and are stored in the item's `datasetMetrics`, so a copy left inside the JSON-LD would
 * silently disagree with them after the user edits.
 */
export function buildCroissantDocument(
  title: string,
  values: Partial<CroissantFormValues>,
  base?: JsonRecord | null,
): JsonRecord {
  const previous = base ?? {};
  const doc: JsonRecord = { ...previous };
  delete doc['platform'];
  delete doc['tanuh:platform'];

  doc['@context'] = previous['@context'] ?? { ...CROISSANT_CONTEXT };
  doc['@type'] = 'sc:Dataset';
  doc['conformsTo'] = previous['conformsTo'] ?? CROISSANT_CONFORMS_TO;
  doc['name'] = title.trim();
  doc['description'] = (values.croissantDescription ?? '').trim();
  setOrDelete(doc, 'version', str(values.croissantVersion));
  setOrDelete(doc, 'license', str(values.croissantLicense));
  setOrDelete(doc, 'url', str(values.croissantUrl));
  setOrDelete(doc, 'citeAs', str(values.croissantCiteAs));
  setOrDelete(doc, 'datePublished', str(values.croissantDatePublished));
  setOrDelete(doc, 'keywords', splitList(values.croissantKeywords ?? ''));

  const creatorText = (values.croissantCreator ?? '').trim();
  if (!creatorText) {
    delete doc['creator'];
  } else if (creatorText !== creatorNames(previous['creator'])) {
    const creators = splitList(creatorText).map((name) => ({ '@type': 'sc:Organization', name }));
    doc['creator'] = creators.length === 1 ? creators[0] : creators;
  }

  // distribution
  const previousDistributions = new Map(
    asArray(previous['distribution']).filter(isRecord).map((d) => [idOf(d), d] as const),
  );
  const distributionIdByName = new Map<string, string>();
  const distributionKindByName = new Map<string, 'FileObject' | 'FileSet'>();
  const distribution = (values.croissantDistributions ?? []).map((entry) => {
    const name = entry.name.trim();
    const id = entry.id.trim() || name;
    distributionIdByName.set(name, id);
    distributionKindByName.set(name, entry.kind);
    const node: JsonRecord = { ...(previousDistributions.get(entry.id.trim()) ?? {}) };
    if (bareType(node['@type']) !== entry.kind) {
      node['@type'] = entry.kind === 'FileSet' ? 'cr:FileSet' : 'sc:FileObject';
    }
    node['@id'] = id;
    node['name'] = name;
    setOrDelete(node, 'description', entry.description.trim());
    node['encodingFormat'] = entry.encodingFormat.trim();
    if (entry.kind === 'FileSet') {
      delete node['contentUrl'];
      delete node['sha256'];
      node['includes'] = entry.includes.trim();
    } else {
      delete node['includes'];
      node['contentUrl'] = entry.contentUrl.trim();
      setOrDelete(node, 'sha256', entry.sha256.trim());
    }
    return node;
  });
  doc['distribution'] = distribution;

  // recordSet
  const previousRecordSets = new Map(
    asArray(previous['recordSet']).filter(isRecord).map((rs) => [idOf(rs), rs] as const),
  );
  doc['recordSet'] = (values.croissantRecordSets ?? []).map((entry) => {
    const name = entry.name.trim();
    const id = entry.id.trim() || name;
    const previousRecordSet = previousRecordSets.get(entry.id.trim());
    const previousFields = new Map(
      asArray(previousRecordSet?.['field']).filter(isRecord).map((f) => [str(f['@id']), f] as const),
    );
    const node: JsonRecord = { ...(previousRecordSet ?? {}) };
    node['@type'] = 'cr:RecordSet';
    node['@id'] = id;
    node['name'] = name;
    setOrDelete(node, 'description', entry.description.trim());
    node['field'] = entry.fields.map((field) => {
      const previousField = field.id.trim() ? previousFields.get(field.id.trim()) : undefined;
      const fieldNode: JsonRecord = { ...(previousField ?? {}) };
      const fieldName = field.name.trim();
      fieldNode['@type'] = 'cr:Field';
      fieldNode['@id'] = field.id.trim() || `${id}/${fieldName}`;
      fieldNode['name'] = fieldName;
      setOrDelete(fieldNode, 'description', field.description.trim());
      setOrDelete(fieldNode, 'dataType', field.dataType.trim());

      const sourceName = field.sourceDistribution.trim();
      if (sourceName) {
        const refKey = distributionKindByName.get(sourceName) === 'FileSet' ? 'fileSet' : 'fileObject';
        const source: JsonRecord = {
          [refKey]: { '@id': distributionIdByName.get(sourceName) ?? sourceName },
        };
        if (field.sourceColumn.trim()) {
          source['extract'] = { column: field.sourceColumn.trim() };
        }
        fieldNode['source'] = source;
      } else if (!previousField || readSimpleSource(previousField['source'], (v) => v) !== null) {
        // No source chosen: drop a builder-representable one the user cleared; keep custom ones.
        delete fieldNode['source'];
      }
      return fieldNode;
    });
    return node;
  });

  return doc;
}

// ---------------------------------------------------------------------------
// catalogue (controlplane DataBank) fields derived from a Croissant document
// ---------------------------------------------------------------------------

/** Placeholder for controlplane-mandatory DataBank fields the builder has no value for. */
export const NOT_AVAILABLE = 'na';

/**
 * Defaults for the DataBank fields the catalogue only accepts fixed values for, so `NOT_AVAILABLE`
 * would make item creation fail.
 */
export const DEFAULT_ORGANIZATION_TYPE = 'Public';
export const DEFAULT_INDUSTRY = 'Health';
export const DEFAULT_LICENSE = 'Open Government License, India';
export const DEFAULT_UPLOAD_FREQUENCY = 'OneTime';

const MIME_TO_FILE_FORMAT: Record<string, string> = {
  'text/csv': 'csv',
  'text/plain': 'txt',
  'text/tab-separated-values': 'tsv',
  'application/json': 'json',
  'application/x-ndjson': 'jsonl',
  'application/zip': 'zip',
  'application/dicom': 'dcm',
  'application/pdf': 'pdf',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/x-parquet': 'parquet',
  'application/vnd.apache.parquet': 'parquet',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/tiff': 'tiff',
};

function fileFormatFromEncoding(encodingFormat: string): string {
  const mime = encodingFormat.trim().toLowerCase();
  if (!mime) {
    return '';
  }
  return (
    MIME_TO_FILE_FORMAT[mime] ??
    mime
      .replace(/^.*\//, '')
      .replace(/^(x-|vnd\.)/, '')
      .replace(/[^a-z0-9.+-]/g, '')
  );
}

export interface CroissantCatalogueFields {
  description: string;
  shortDescription: string;
  tags: string[];
  license: string;
  yearRange: string;
  fileFormat: string;
}

/**
 * Values for the DataBank fields the controlplane requires (docs/openapi.yaml `exampleEntityDataBank`),
 * taken from the Croissant document where it has an equivalent. Free-text fields fall back to
 * `NOT_AVAILABLE`; `license` and `yearRange` are validated by the catalogue, so they always get a
 * valid value (the document's own licence stays in the stored Croissant JSON).
 */
export function deriveCatalogueFieldsFromCroissant(doc: JsonRecord | null | undefined): CroissantCatalogueFields {
  const source = doc ?? {};
  const description = str(source['description']);
  const keywords = asArray(source['keywords']).map(str).filter(Boolean);
  const year = /^\d{4}/.exec(str(source['datePublished']))?.[0] ?? String(new Date().getFullYear());
  const firstEncoding = asArray(source['distribution'])
    .filter(isRecord)
    .map((d) => str(d['encodingFormat']))
    .find(Boolean);

  return {
    description: description || NOT_AVAILABLE,
    shortDescription: description.length > 200 ? `${description.slice(0, 197).trimEnd()}...` : description || NOT_AVAILABLE,
    tags: keywords.length > 0 ? keywords : [NOT_AVAILABLE],
    license: DEFAULT_LICENSE,
    yearRange: `${year}-${year}`,
    fileFormat: fileFormatFromEncoding(firstEncoding ?? '') || NOT_AVAILABLE,
  };
}

/** Minimal valid Croissant document for the "Download template" button. */
export function buildCroissantTemplate(): JsonRecord {
  return {
    '@context': { ...CROISSANT_CONTEXT },
    '@type': 'sc:Dataset',
    conformsTo: CROISSANT_CONFORMS_TO,
    name: 'my_dataset',
    description: 'What the data is, how it was collected or labelled, and how it may be used.',
    version: '1.0.0',
    license: 'https://creativecommons.org/licenses/by/4.0/',
    url: 'https://example.org/my_dataset',
    citeAs: 'Author, A. (2026). My dataset.',
    creator: { '@type': 'sc:Organization', name: 'Your organisation' },
    datePublished: '2026-01-01',
    keywords: ['health', 'imaging'],
    distribution: [
      {
        '@type': 'sc:FileObject',
        '@id': 'labels.csv',
        name: 'labels.csv',
        description: 'One row per record.',
        contentUrl: 'labels.csv',
        encodingFormat: 'text/csv',
        sha256: 'replace-with-the-sha256-of-labels.csv',
      },
    ],
    recordSet: [
      {
        '@type': 'cr:RecordSet',
        '@id': 'records',
        name: 'records',
        description: 'One record per case.',
        field: [
          {
            '@type': 'cr:Field',
            '@id': 'records/case_id',
            name: 'case_id',
            dataType: 'sc:Text',
            source: { fileObject: { '@id': 'labels.csv' }, extract: { column: 'case_id' } },
          },
          {
            '@type': 'cr:Field',
            '@id': 'records/label',
            name: 'label',
            dataType: 'sc:Integer',
            source: { fileObject: { '@id': 'labels.csv' }, extract: { column: 'label' } },
          },
        ],
      },
    ],
  };
}

export function downloadCroissantTemplate(filename = 'croissant-template.json'): void {
  const blob = new Blob([JSON.stringify(buildCroissantTemplate(), null, 2)], {
    type: 'application/json',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
